/**
 * ProfileAvatar — reusable circular avatar component.
 *
 * Shows the profile image if available, otherwise falls back to initials
 * derived from `name`, or a generic user icon if neither is available.
 */
import React, { useState } from 'react';
import ProfileImagePreviewModal from './ProfileImagePreviewModal';

const SIZES = {
  xs: { wrapper: 'h-6 w-6', text: 'text-xs', icon: 'h-3 w-3', edit: 'h-2 w-2' },
  sm: { wrapper: 'h-8 w-8', text: 'text-xs', icon: 'h-4 w-4', edit: 'h-3 w-3' },
  md: { wrapper: 'h-10 w-10', text: 'text-sm', icon: 'h-5 w-5', edit: 'h-4 w-4' },
  lg: { wrapper: 'h-14 w-14', text: 'text-base', icon: 'h-7 w-7', edit: 'h-5 w-5' },
  xl: { wrapper: 'h-20 w-20', text: 'text-xl', icon: 'h-10 w-10', edit: 'h-6 w-6' },
};

const ACCENT = '#7d6633';

function getInitials(name) {
  if (!name) return '';
  const words = name.trim().split(/\s+/);
  if (words.length === 1) return words[0].charAt(0).toUpperCase();
  return (words[0].charAt(0) + words[words.length - 1].charAt(0)).toUpperCase();
}

const ProfileAvatar = ({
  name = '',
  avatarUrl,
  size = 'md',
  onClick,
  showEditHint = false,
  previewable = false,
  className = '',
}) => {
  const [imgError, setImgError] = useState(false);
  const [hovered, setHovered] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);

  const sz = SIZES[size] || SIZES.md;
  const initials = getInitials(name);
  const canPreview = previewable && !!avatarUrl && !imgError;
  const isClickable = !!onClick || canPreview;
  const showOverlay = showEditHint && hovered && isClickable;

  const handleClick = (event) => {
    if (onClick) {
      onClick(event);
    }

    if (canPreview) {
      setPreviewOpen(true);
    }
  };

  return (
    <>
      <div
        className={`relative flex-shrink-0 rounded-full overflow-hidden select-none ${sz.wrapper} ${
          isClickable ? 'cursor-pointer' : ''
        } ${className}`}
        onClick={isClickable ? handleClick : undefined}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        title={canPreview ? `View ${name || 'profile'} photo` : (name || undefined)}
      >
        {avatarUrl && !imgError ? (
          <img
            src={avatarUrl}
            alt={name || 'Profile'}
            onError={() => setImgError(true)}
            className="h-full w-full object-cover"
            draggable={false}
          />
        ) : initials ? (
          <div
            className={`h-full w-full flex items-center justify-center font-semibold text-white ${sz.text}`}
            style={{ background: ACCENT }}
          >
            {initials}
          </div>
        ) : (
          <div className="h-full w-full flex items-center justify-center bg-zinc-200 dark:bg-zinc-700">
            <svg
              className={`${sz.icon} text-zinc-400 dark:text-zinc-500`}
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z"
                clipRule="evenodd"
              />
            </svg>
          </div>
        )}

        {showOverlay && (
          <div
            className="absolute inset-0 flex items-center justify-center rounded-full bg-black/50 transition-opacity duration-200"
            style={{ opacity: hovered ? 1 : 0 }}
          >
            <svg
              className={`${sz.edit} text-white`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z"
              />
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 13a3 3 0 11-6 0 3 3 0 016 0z"
              />
            </svg>
          </div>
        )}
      </div>

      <ProfileImagePreviewModal
        open={previewOpen}
        imageUrl={avatarUrl}
        alt={name || 'Profile photo'}
        onClose={() => setPreviewOpen(false)}
      />
    </>
  );
};

export default ProfileAvatar;
